import { FC, useEffect, useMemo } from "react";
import useNavigateTo from "hooks/useNavigateTo";
// i18next
import { useTranslation } from "react-i18next";
// Redux
import { useAppSelector } from "hooks/useStore";
// Selectors
import { selectQuiz } from "store/quiz/quizSelectors";
import { selectMaxScore, selectPreviewPageMode, selectTotalScore } from "store/player/playerSelectors";
// Components
import AppBar from "components/AppBar";
import { Button } from "components/Buttons";
import PoweredBy from "components/PoweredBy";
// Icons
import { GiTrophyCup } from "react-icons/gi";

const ALLOWED_CTA_PROTOCOLS = ['http:', 'https:'];

const isSafeCtaUrl = (value?: string) => {
  if (!value) return false;
  try {
    return ALLOWED_CTA_PROTOCOLS.includes(new URL(value, window.location.origin).protocol);
  } catch {
    return false;
  }
};

const ScoringResultPage:FC = () => {
  const { t } = useTranslation();

  const navigateTo = useNavigateTo();

  const quiz = useAppSelector(selectQuiz);
  const totalScore = useAppSelector(selectTotalScore);
  const maxScore = useAppSelector(selectMaxScore);
  const previewPageMode = useAppSelector(selectPreviewPageMode);

  const onClick = () => navigateTo('register');

  useEffect(() => {
    if (!quiz.scoringResultPage?.enabled && !previewPageMode) onClick();
    // eslint-disable-next-line
  }, []);

  const percentage = useMemo(() => (maxScore ? (totalScore / maxScore) * 100 : 0), [totalScore, maxScore]);

  const matchedBand = useMemo(() => {
    const bands = quiz.scoringResultPage?.bands || [];
    return [...bands]
      .sort((a, b) => b.minPercentage - a.minPercentage)
      .find(band => percentage >= band.minPercentage);
  }, [percentage, quiz.scoringResultPage?.bands]);

  const { primaryColor } = quiz.scoringResultPage || {};

  if (!quiz.scoringResultPage?.enabled && !previewPageMode) return null;
  if (!matchedBand) return null;
  return (
    <div
      className="p-4 flex flex-col w-full h-full bg-gray-200 overflow-y-auto"
      style={{ background: quiz.backgroundImage ? `url('${quiz.backgroundImage}') center / cover` : '' }}
    >
      <AppBar />
      <div className="flex-1 text-primary pt-8">
        <GiTrophyCup className="text-7xl mx-auto mb-4" style={{ color: primaryColor }} />
        <h1 className="text-center text-3xl font-bold uppercase mb-2" style={{ color: primaryColor }}>{matchedBand.title}</h1>

        {quiz.scoringResultPage.image ? (
          <img src={quiz.scoringResultPage.image} alt="" className="w-[75%] mx-auto mb-8 shadow-primary" />
        ) : null}

        <div className="mb-8">
          <p className="text-center text-lg font-bold uppercase" style={{ color: primaryColor }}>{t('pages.scoringResult.scoreTitle')}</p>
          <p className="text-center text-2xl font-bold uppercase" style={{ color: primaryColor }}>{`${Math.round(percentage)}%`}</p>
          <p className={`text-center text-2xl font-bold uppercase mb-4`}>{`${totalScore} / ${maxScore} ${t('labels.points')}`}</p>
          {matchedBand.text ? (
            <p className="text-center text-xl">{matchedBand.text}</p>
          ) : null}
        </div>

        {isSafeCtaUrl(matchedBand.ctaUrl) ? (
          <Button
            className="w-full !text-xl primary-button normal-case mb-4"
            size="small"
            onClick={() => window.open(matchedBand.ctaUrl, '_blank', 'noopener,noreferrer')}
          >
            {matchedBand.ctaButtonTitle || t('buttons.visit')}
          </Button>
        ) : null}

        <Button
          className="w-full !text-xl flex items-center justify-center gap-3 uppercase primary-button mb-4"
          size="small"
          onClick={onClick}
        >
          {t('buttons.next')}
        </Button>
      </div>
      <PoweredBy />
    </div>
  )
}

export default ScoringResultPage;
